import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Inbox, Check, X, GitMerge } from "lucide-react";
import { formatDisplayName } from "@/lib/names";

function PendingRow({ p, onApprove, onDismiss, busy }) {
  const dupes = p.possible_duplicates || [];
  return (
    <div className="p-4" style={{ borderTop: "1px solid var(--border-subtle)" }} data-testid={`review-row-${p.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-medium text-sm" style={{ color: "var(--ink)" }}>{formatDisplayName(p)}</div>
          <div className="text-[0.7rem] mt-0.5" style={{ color: "var(--muted)" }}>
            {[p.phone, p.email, p.source, new Date(p.received_at).toLocaleDateString()].filter(Boolean).join(" · ")}
          </div>
          {p.notes && <div className="text-xs mt-1" style={{ color: "var(--ink-2)" }}>{p.notes}</div>}
        </div>
        <div className="flex gap-2 shrink-0">
          {dupes.length === 0 && (
            <button onClick={() => onApprove(p, {})} disabled={busy} className="btn-primary text-xs" style={{ padding: "0.4rem 0.9rem" }} data-testid={`review-approve-${p.id}`}><Check size={12} /> Add to directory</button>
          )}
          <button onClick={() => onDismiss(p)} disabled={busy} className="btn-ghost text-xs" style={{ padding: "0.4rem 0.9rem" }} data-testid={`review-dismiss-${p.id}`}><X size={12} /> Dismiss</button>
        </div>
      </div>
      {dupes.length > 0 && (
        <div className="mt-3 p-3 rounded-md" style={{ background: "var(--surface-hover)", borderLeft: "3px solid var(--ember)" }} data-testid={`review-dupes-${p.id}`}>
          <div className="text-xs mb-2" style={{ color: "var(--ink-2)" }}>
            <strong style={{ color: "var(--ember)" }}>Possible duplicate.</strong> This looks like someone already in the directory:
          </div>
          <div className="space-y-1.5">
            {dupes.map((d) => (
              <div key={d.id} className="flex items-center justify-between gap-2 flex-wrap text-xs">
                <span style={{ color: "var(--ink)" }}>{formatDisplayName(d)} <span style={{ color: "var(--muted)" }}>· {[d.phone, d.email, d.lifecycle_status].filter(Boolean).join(" · ")}</span></span>
                <button onClick={() => onApprove(p, { merge_into: d.id })} disabled={busy} className="btn-ghost text-xs" style={{ padding: "0.3rem 0.7rem" }} data-testid={`review-merge-${p.id}-${d.id}`}><GitMerge size={11} /> Merge into this person</button>
              </div>
            ))}
          </div>
          <button onClick={() => onApprove(p, { force: true })} disabled={busy} className="text-xs underline mt-2" style={{ color: "var(--terracotta)" }} data-testid={`review-add-anyway-${p.id}`}>Different person — add as new</button>
        </div>
      )}
    </div>
  );
}

export default function ReviewQueue({ orgId, onChanged }) {
  const [rows, setRows] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const params = { org_id: orgId };

  const load = () => api.get("/coord/directory/pending", { params }).then((r) => setRows(r.data.pending || [])).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (orgId) load(); }, [orgId]);

  const act = async (p, fn, msg) => {
    setBusyId(p.id);
    try { await fn(); toast.success(msg); await load(); onChanged?.(); }
    catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409) { toast.error("Possible duplicate found — choose merge or add as new."); await load(); }
      else toast.error(typeof d === "string" ? d : "Couldn't update.");
    } finally { setBusyId(null); }
  };
  const approve = (p, body) => act(p, () => api.post(`/coord/directory/pending/${p.id}/approve`, body, { params }), body.merge_into ? "Merged into the existing person." : "Added to the directory.");
  const dismiss = (p) => act(p, () => api.delete(`/coord/directory/pending/${p.id}`, { params }), "Dismissed.");

  if (rows.length === 0) return null;
  return (
    <section className="mb-8 rounded-md overflow-hidden" style={{ background: "var(--bg-alt)", border: "1px solid var(--ember)" }} data-testid="directory-review-queue">
      <div className="px-4 py-3 flex items-center gap-2" style={{ background: "var(--surface-hover)" }}>
        <Inbox size={15} style={{ color: "var(--ember)" }} />
        <span className="font-medium text-sm" style={{ color: "var(--ink)" }}>Review queue</span>
        <span className="text-[0.6rem] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full" style={{ background: "var(--ember)", color: "#FFF" }} data-testid="review-count">{rows.length}</span>
        <span className="text-xs ml-2" style={{ color: "var(--muted)" }}>People who signed themselves up by QR, kiosk, or check-in. Approve them to add them to the directory.</span>
      </div>
      {rows.map((p) => <PendingRow key={p.id} p={p} busy={busyId === p.id} onApprove={approve} onDismiss={dismiss} />)}
    </section>
  );
}
